import React, { useEffect, useState } from "react";
import { nanoid } from "nanoid";
import { generateChordSlug } from "./CreateEvent/utils";
import { apiClient } from "../../utils/api"; // Import the API client
import { toast } from "react-toastify"; // Import toast from react-toastify
import { useVenueHistory } from "../../hooks/useVenueHistory";
import type { VenueData } from "../../types/venue";

// 既存インポート
import BasicInfo from "./CreateEvent/BasicInfo";
import VenueInfo from "./CreateEvent/VenueInfo";
import ImageUpload from "./CreateEvent/ImageUpload";
import DateTimePicker from "./CreateEvent/DateTimePicker";
import OpeningTimes from "./CreateEvent/OpeningTimes";
import TicketTypes from "./CreateEvent/TicketTypes";
import EventSettings from "./CreateEvent/EventSettings";
import BackgroundImageUpload, {
	BackgroundImageData,
} from "./CreateEvent/BackgroundImageUpload";
import EventCautions, {
	CautionItem,
} from "./CreateEvent/EventCautions";
import HashtagInput from "./HashtagInput";

/**
 * 開場・開演時間
 */
type OpeningTime = {
	id: string;
	external_id?: string; // バックエンドで管理されるexternal_id
	openTime: string;
	startTime: string;
	endTime: string;
	description?: string;
};

/**
 * チケットタイプ
 */
type TicketType = {
	/** フロントエンド用の一時ID */
	id: string;
	/** バックエンドで管理されるexternal_id */
	external_id?: string;
	name: string;
	description: string;
	isFree: boolean;
	price: number;
	hasLimit: boolean;
	quantity: number;
	publicationDateTime: string;
	saleStartDateTime: string;
	saleEndDateTime: string;
	/** OpeningTime の外部IDリスト */
	openingTimeIds: string[];
	soldTickets: number;
};

type FormDataType = {
	title: string;
	slug: string;
	description: string;
	date: string;
	venue: VenueData;
	images: {
		id: string;
		file: File | null;
		previewUrl: string;
		order: number;
		objectName?: string;
		publicUrl?: string;
		uploading?: boolean;
		error?: string;
	}[];
	backgroundImage: BackgroundImageData | null;
	openingTimes: OpeningTime[];
	ticketTypes: TicketType[];
	publicationDateTime: string;
	saleStartDateTime: string;
	saleEndDateTime: string;
	hashtags: string[];
	status: "draft" | "published";
	cautions: CautionItem[];
};

const CreateEvent: React.FC = () => {
	// メインフォーム state
	const [formData, setFormData] = useState<FormDataType>({
		title: "",
		slug: generateChordSlug(),
		description: "",
		date: "",
		venue: {
			venueName: "",
			postalCode: "",
			prefecture: "",
			city: "",
			address: "",
		},
		images: [],
		backgroundImage: null,
		openingTimes: [
			{
				id: nanoid(8),
				openTime: "18:00",
				startTime: "19:00",
				endTime: "21:00",
				description: "",
			},
		],
		ticketTypes: [
			{
				id: nanoid(8), // フロントエンド用一時ID
				external_id: undefined, // バックエンドで割り当てられる
				name: "",
				description: "",
				isFree: false,
				price: 1000,
				hasLimit: false,
				quantity: 100,
				publicationDateTime: "",
				saleStartDateTime: "",
				saleEndDateTime: "",
				openingTimeIds: [],
				soldTickets: 0,
			},
		],
		publicationDateTime: "",
		saleStartDateTime: "",
		saleEndDateTime: "",
		hashtags: [],
		status: "draft",
		cautions: [{ id: nanoid(8), content: "" }],
	});

	const { venueHistory, addVenueToHistory } = useVenueHistory();

	// スラッグ編集モード
const [isEditingSlug, setIsEditingSlug] = useState(false);
	const [slugCheckStatus, setSlugCheckStatus] = useState<
		"idle" | "checking" | "available" | "taken" | "error"
	>("idle");
	const [slugCheckMessage, setSlugCheckMessage] = useState<string | null>(null);
	const isSlugCompletionDisabled = isEditingSlug && slugCheckStatus === "taken";

	// Loading state
	const [isSubmitting, setIsSubmitting] = useState(false);

	// 入力変更ハンドラ（BasicInfo 用）
	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// スラッグ編集モード切り替え
	const toggleSlugEditMode = () => {
		if (isEditingSlug && slugCheckStatus === "taken") {
			return;
		}
		setIsEditingSlug((prev) => {
			const next = !prev;
			if (!next) {
				setSlugCheckStatus("idle");
				setSlugCheckMessage(null);
		}
		return next;
	});
};

	// スラッグ再生成
const regenerateSlug = () => {
	setFormData((prev) => ({
		...prev,
		slug: generateChordSlug(),
	}));
};

// スラッグ重複チェック
useEffect(() => {
	if (!isEditingSlug || !formData.slug) {
		setSlugCheckStatus("idle");
		setSlugCheckMessage(null);
		return;
	}

	let isActive = true;
	setSlugCheckStatus("checking");
	setSlugCheckMessage("スラッグを確認しています...");

	const debounceId = setTimeout(async () => {
		try {
			const response = await apiClient.get(
				"/organizer/events/slug/check",
				{ params: { slug: formData.slug } }
			);

			if (!isActive) return;

			if (response.data.available) {
				setSlugCheckStatus("available");
				setSlugCheckMessage("このスラッグは使用できます");
			} else {
				setSlugCheckStatus("taken");
				setSlugCheckMessage("このスラッグは既に使用されています");
			}
		} catch (error) {
			console.error("Failed to validate slug", error);
			if (!isActive) return;
			setSlugCheckStatus("error");
			setSlugCheckMessage("スラッグの確認に失敗しました");
		} finally {
			// status already updated above
		}
	}, 500);

	return () => {
		isActive = false;
		clearTimeout(debounceId);
	};
}, [formData.slug, isEditingSlug]);

	// 住所入力が変更されたら更新

	// 開場・開演時間の変更ハンドラ
	const handleOpeningTimeChange = (
		id: string,
		field: keyof OpeningTime,
		value: string
	) => {
		if (field === "id") return;
		setFormData((prev) => ({
			...prev,
			openingTimes: prev.openingTimes.map((time) =>
				time.id === id ? { ...time, [field]: value } : time
			),
		}));
	};

	// 開演時間だけを変更するハンドラ (OpeningTimesコンポーネント用)
	const handleStartTimeChange = (id: string, value: string) => {
		handleOpeningTimeChange(id, "startTime", value);
	};

	// 開場・開演時間追加
	const addOpeningTime = () => {
		// 新規追加時は一時IDを生成するが、external_idとしても使う
		const newId = nanoid(10);
		setFormData((prev) => ({
			...prev,
			openingTimes: [
				...prev.openingTimes,
				{
					id: newId, // フロントエンド用ID
					external_id: newId, // 同じIDをexternal_idにも設定
					openTime: "18:00",
					startTime: "19:00",
					endTime: "21:00",
					description: "",
				},
			],
		}));
	};

	// 開場・開演時間削除
	const removeOpeningTime = (id: string) => {
		// 削除する開場時間を探す
		const openingTimeToRemove = formData.openingTimes.find(
			(ot) => ot.id === id
		);
		if (!openingTimeToRemove) return;

		// external_id があればそれを使う、なければ ID を使う
		const idToFilter = openingTimeToRemove.external_id || id;

		setFormData((prev) => ({
			...prev,
			openingTimes: prev.openingTimes.filter((time) => time.id !== id),
			ticketTypes: prev.ticketTypes.map((ticket) => ({
				...ticket,
				// external_id または id で絞り込む
				openingTimeIds: ticket.openingTimeIds.filter(
					(timeId) => timeId !== idToFilter
				),
			})),
		}));
	};

	// チケットタイプの変更ハンドラ
	const handleTicketTypeChange = (
		id: string,
		field: keyof TicketType,
		value: any
	) => {
		// 価格の場合は整数値に丸める
		if (field === "price" && typeof value === "number") {
			value = Math.round(value);
		}

		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.map((ticket) =>
				ticket.id === id ? { ...ticket, [field]: value } : ticket
			),
		}));
	};

	// チケットタイプの追加
	const addTicketType = () => {
		setFormData((prev) => ({
			...prev,
			ticketTypes: [
				...prev.ticketTypes,
				{
					id: nanoid(8), // フロントエンド用一時ID
					external_id: undefined, // バックエンドで割り当てられる
					name: "",
					description: "",
					isFree: false,
					price: 1000,
					hasLimit: false,
					quantity: 100,
					publicationDateTime: "",
					saleStartDateTime: "",
					saleEndDateTime: "",
					openingTimeIds: [],
					soldTickets: 0,
				},
			],
		}));
	};

	// チケットタイプの削除
	const removeTicketType = (id: string) => {
		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.filter((ticket) => ticket.id !== id),
		}));
	};

	// チケットと開演時間の関連づけ
	const toggleOpeningTimeForTicket = (
		ticketId: string,
		openingTimeId: string
	) => {
		// 選択された開場時間を取得
		const selectedOpeningTime = formData.openingTimes.find(
			(ot) => ot.id === openingTimeId
		);
		if (!selectedOpeningTime) return;

		// external_id を優先的に使用する
		const idToToggle = selectedOpeningTime.external_id || openingTimeId;

		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.map((ticket) => {
				if (ticket.id === ticketId) {
					const openingTimeIds = [...ticket.openingTimeIds];
					const index = openingTimeIds.indexOf(idToToggle);

					if (index === -1) {
						openingTimeIds.push(idToToggle);
					} else {
						openingTimeIds.splice(index, 1);
					}

					return {
						...ticket,
						openingTimeIds,
					};
				}
				return ticket;
			}),
		}));
	};

	// フォーム送信
	const handleSubmit = async (
		e: React.FormEvent,
		status: "draft" | "published"
	) => {
		e.preventDefault();

		setFormData((prev) => ({
			...prev,
			status,
		}));

		// 必須項目のバリデーション
		if (!formData.title) {
			toast.error("イベントタイトルは必須項目です");
			return;
		}

		if (!formData.date) {
			toast.error("開催日は必須項目です");
			return;
		}

		// 住所情報のバリデーション（会場名はnullable）

		if (!formData.venue.postalCode) {
			toast.error("郵便番号は必須項目です");
			return;
		}
		if (!formData.venue.prefecture) {
			toast.error("都道府県は必須項目です");
			return;
		}
		if (!formData.venue.city) {
			toast.error("市区町村は必須項目です");
			return;
		}
		if (!formData.venue.address) {
			toast.error("番地と建物名は必須項目です");
			return;
		}

		// チケット情報のバリデーション
		for (const ticket of formData.ticketTypes) {
			// 必須項目: チケット名
			if (!ticket.name.trim()) {
				toast.error("全てのチケットに名前を設定してください");
				return;
			}

			// 有料チケットの場合、価格は必須
			if (!ticket.isFree && (!ticket.price || ticket.price <= 0)) {
				toast.error(
					`チケット「${ticket.name || "無題"}」の価格を設定してください`
				);
				return;
			}

			// 制限ありの場合、枚数は必須
			if (ticket.hasLimit && (!ticket.quantity || ticket.quantity <= 0)) {
				toast.error(
					`チケット「${ticket.name || "無題"}」の販売枚数を設定してください`
				);
				return;
			}

			// 開場/開演情報との関連付けは必須
			if (ticket.openingTimeIds.length === 0) {
				toast.error(
					`チケット「${
						ticket.name || "無題"
					}」に対応する開場/開演情報を選択してください`
				);
				return;
			}
		}

		// Set submitting state
		setIsSubmitting(true);

		if (formData.backgroundImage?.uploading) {
			toast.error("背景画像のアップロード完了をお待ちください");
			setIsSubmitting(false);
			return;
		}

		// 画像アップロード用に FormData を使う
		const submitData = new FormData();

		// イベント基本情報
		submitData.append("title", formData.title);
		submitData.append("slug", formData.slug);
		submitData.append("description", formData.description);
		submitData.append("date", formData.date);
		submitData.append("status", status);

		// 会場情報
		submitData.append("venue_name", formData.venue.venueName);
		submitData.append("postal_code", formData.venue.postalCode);
		submitData.append("prefecture", formData.venue.prefecture);
		submitData.append("city", formData.venue.city);
		submitData.append("address", formData.venue.address);
		submitData.append("hashtags", JSON.stringify(formData.hashtags.slice(0, 10)));

		// 開場・開演時間（JSON文字列）
		// external_id があれば使用し、なければフロントエンド側のIDを使う
		const openingTimesPayload = formData.openingTimes.map((time) => ({
			id: time.external_id || time.id, // external_id を優先して送信
			external_id: time.external_id, // バックエンドが持つ external_id を明示的に送信
			open_time: time.openTime,
			start_time: time.startTime,
			end_time: time.endTime,
			description: time.description || "",
		}));
		submitData.append("openingTimes", JSON.stringify(openingTimesPayload));

		// チケット（JSON文字列）
		const ticketTypesPayload = formData.ticketTypes.map((ticket) => ({
			name: ticket.name,
			description: ticket.description || "",
			is_free: ticket.isFree,
			price: ticket.price,
			has_limit: ticket.hasLimit,
			quantity: ticket.quantity,
			publication_date_time: ticket.publicationDateTime || null,
			sale_start_date_time: ticket.saleStartDateTime || null,
			sale_end_date_time: ticket.saleEndDateTime || null,
			// external_id が既に存在する場合は送信（更新用）
			external_id: ticket.external_id,
			// openingTimeIds はexternal_idによる参照
			opening_time_ids: ticket.openingTimeIds.filter(Boolean),
		}));
		submitData.append("ticketTypes", JSON.stringify(ticketTypesPayload));

		const cautionPayload = formData.cautions
			.map((item, index) => ({
				content: item.content.trim(),
				position: index,
			}))
			.filter((item) => item.content.length > 0);

		submitData.append("cautions", JSON.stringify(cautionPayload));

		// 公開設定
		if (formData.publicationDateTime) {
			submitData.append("publication_date_time", formData.publicationDateTime);
		}
		if (formData.saleStartDateTime) {
			submitData.append("sale_start_date_time", formData.saleStartDateTime);
		}
		if (formData.saleEndDateTime) {
			submitData.append("sale_end_date_time", formData.saleEndDateTime);
		}

		if (formData.backgroundImage?.publicUrl) {
			submitData.append(
				"background_image_url",
				formData.backgroundImage.publicUrl
			);
		}

		// 画像関連
		// 既存画像の処理 (GCSに既にアップロード済みの画像)
		const existingImagesData = formData.images
			.filter((img) => img.publicUrl && img.objectName)
			.map((img) => ({
				public_url: img.publicUrl,
				object_name: img.objectName,
				order: img.order,
			}));
		if (existingImagesData.length > 0) {
			submitData.append("existing_images", JSON.stringify(existingImagesData));
		}

		// 新規アップロード画像の処理
		const newImages = formData.images.filter(
			(img) => img.file && !img.publicUrl
		);
		newImages.forEach((img, index) => {
			if (img.file) {
				submitData.append(`images[${index}]`, img.file);
				submitData.append(`imageOrders[${index}]`, String(img.order));
			}
		});

		// 開発用ログ出力 (問題調査用)
		console.log("==== 送信データ(FormData) ====");
		submitData.forEach((value, key) => {
			if (value instanceof File) {
				console.log(`${key} (File): ${value.name}`);
			} else {
				console.log(`${key}: ${value}`);
			}
		});

		try {
			// API リクエスト送信 - apiClient を使用して multipart/form-data リクエストを送信
			// 正しいエンドポイントは /organizer/events
			// multipart/form-data の場合は Content-Type ヘッダーを手動で設定しない
			// axios が自動的に境界値を設定する
			const response = await apiClient.post("/organizer/events", submitData);

			console.log("Event created:", response.data);

			// Show success message
			toast.success(
				status === "published"
					? "イベントが公開されました！"
					: "イベントが下書き保存されました！"
			);

			addVenueToHistory(formData.venue);

			// Reset form if needed or redirect
			// window.location.href = `/organizer/events/${response.data.event.external_id}`;
		} catch (error: any) {
			console.error("Failed to create event:", error);

			// Handle API errors
			if (error.response) {
				// API returned an error response
				const responseData = error.response.data;

				if (responseData.errors) {
					// Handle validation errors
					const firstError = Object.values(responseData.errors)[0];
					toast.error(
						Array.isArray(firstError) ? firstError[0] : String(firstError)
					);
				} else if (responseData.message) {
					// Handle general error message
					toast.error(responseData.message);
				} else {
					toast.error(`エラーが発生しました (${error.response.status})`);
				}
			} else {
				// Network error or other issues
				toast.error(
					"通信エラーが発生しました。ネットワーク接続を確認してください。"
				);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="mb-8">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
					新規イベント作成
				</h2>
				<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
					イベントの詳細情報を入力してください
				</p>
			</div>

			<form onSubmit={(e) => handleSubmit(e, formData.status)}>
				<div className="space-y-6">
					{/* イベント基本情報 */}
				<BasicInfo
					formData={{
						title: formData.title,
						slug: formData.slug,
						description: formData.description,
					}}
					handleInputChange={handleInputChange}
					isEditingSlug={isEditingSlug}
					toggleSlugEditMode={toggleSlugEditMode}
					regenerateSlug={regenerateSlug}
					slugCheckStatus={slugCheckStatus}
					slugCheckMessage={slugCheckMessage}
					slugCompletionDisabled={isSlugCompletionDisabled}
				/>

					{/* 開催日 */}
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white pb-2">
							開催日
						</h3>
						<DateTimePicker
							id="event-date"
							label="開催日"
							value={formData.date}
							onChange={(value: string) =>
								setFormData((prev) => ({ ...prev, date: value }))
							}
							required={true}
							placeholder="開催日を選択してください"
							dateOnly={true}
						/>
					</div>

					{/* 会場情報 */}
					<VenueInfo
						venueData={formData.venue}
						onChange={(venueData) =>
							setFormData((prev) => ({
								...prev,
								venue: venueData,
							}))
						}
						required={true}
						venueHistory={venueHistory}
					/>

					{/* 画像アップロード */}
					<ImageUpload
						formData={{ images: formData.images }}
						setFormData={setFormData}
					/>

					{/* 背景画像アップロード */}
					<BackgroundImageUpload
						value={formData.backgroundImage}
						onChange={(image) =>
							setFormData((prev) => ({
								...prev,
								backgroundImage: image,
							}))
						}
					/>

					{/* 開場・開演時間 */}
					<OpeningTimes
						openingTimes={formData.openingTimes}
						handleOpeningTimeChange={handleOpeningTimeChange}
						addOpeningTime={addOpeningTime}
						removeOpeningTime={removeOpeningTime}
						handleStartTimeChange={handleStartTimeChange}
					/>

					{/* チケットタイプ */}
					<TicketTypes
						ticketTypes={formData.ticketTypes}
						openingTimes={formData.openingTimes}
						handleTicketTypeChange={handleTicketTypeChange}
						addTicketType={addTicketType}
						removeTicketType={removeTicketType}
						toggleOpeningTimeForTicket={toggleOpeningTimeForTicket}
					/>

					<EventCautions
						items={formData.cautions}
						onChange={(next) =>
							setFormData((prev) => ({
								...prev,
								cautions: next,
							}))
						}
					/>

					{/* ハッシュタグ */}
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
						<HashtagInput
							value={formData.hashtags}
							onChange={(hashtags) =>
								setFormData((prev) => ({ ...prev, hashtags }))
							}
							max={10}
							description="イベント検索やSNSで使うキーワードを最大10個まで設定できます（Enter／カンマで追加）"
							placeholder="例）ライブ, ジャズ, 東京"
						/>
					</div>

					{/* イベント公開設定 */}
				<EventSettings
					formData={{
						publicationDateTime: formData.publicationDateTime,
						saleStartDateTime: formData.saleStartDateTime,
						saleEndDateTime: formData.saleEndDateTime,
					}}
					setFormData={setFormData}
					eventDate={formData.date}
				/>

					{/* ボタン */}
					<div className="flex justify-end space-x-4 mt-8">
						<button
							type="button"
							onClick={(e) => handleSubmit(e, "draft")}
							disabled={isSubmitting}
							className={`px-6 py-3 bg-gray-500 dark:bg-gray-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md transform transition-all duration-200
                     ${
												isSubmitting
													? "opacity-50 cursor-not-allowed"
													: "hover:bg-gray-600 dark:hover:bg-gray-700 hover:-translate-y-0.5"
											}`}
						>
							{isSubmitting ? "保存中..." : "下書き保存"}
						</button>
						<button
							type="button"
							onClick={(e) => handleSubmit(e, "published")}
							disabled={isSubmitting}
							className={`px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md transform transition-all duration-200
                     ${
												isSubmitting
													? "opacity-50 cursor-not-allowed"
													: "hover:from-violet-600 hover:to-purple-700 hover:-translate-y-0.5"
											}`}
						>
							{isSubmitting ? "公開中..." : "イベントを公開"}
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default CreateEvent;
